import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Skincare", "Haircare", "Personal Care", "Baby Care", "Ayurveda",
  "Nutraceuticals", "Wellness", "Spa Products", "Hospitality", "Other",
];

const initial = { name: "", email: "", phone: "", company: "", category: "", message: "" };

const TextField = ({ id, label, testId, value, onChange, placeholder, type = "text" }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-navy">{label}</Label>
    <Input data-testid={testId} id={id} type={type} value={value} onChange={onChange} placeholder={placeholder} className="bg-white border-navy/15" />
  </div>
);

const CategoryField = ({ value, onChange }) => (
  <div className="space-y-1.5">
    <Label className="text-navy">Product Category *</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="contact-category-select" className="bg-white border-navy/15">
        <SelectValue placeholder="Select a category" />
      </SelectTrigger>
      <SelectContent className="bg-cream border-navy/10">
        {CATEGORIES.map((c) => (
          <SelectItem key={c} value={c} data-testid={`contact-category-option-${c.toLowerCase().replace(/\s/g, "-")}`}>{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const SubmitButton = ({ submitting }) => (
  <button
    data-testid="contact-submit-btn"
    type="submit"
    disabled={submitting}
    className="group btn-shine w-full inline-flex items-center justify-center gap-3 rounded-full bg-navy text-ivory px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
  >
    {submitting ? <Loader2 size={16} className="animate-spin" /> : (
      <>
        Request a Free Call
        <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
      </>
    )}
  </button>
);

export default function ContactForm({ onSuccess, idPrefix = "lead" }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.category) {
      toast.error("Please fill in your name, email, phone and product category.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thank you! We'll be in touch within one business day.");
      setForm(initial);
      onSuccess?.();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="mt-2 space-y-4 font-body">
      <div className="grid sm:grid-cols-2 gap-4">
        <TextField id={`${idPrefix}-name`} label="Name *" testId="contact-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" />
        <TextField id={`${idPrefix}-email`} label="Email *" testId="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@brand.com" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <TextField id={`${idPrefix}-phone`} label="Phone *" testId="contact-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" />
        <TextField id={`${idPrefix}-company`} label="Company" testId="contact-company-input" value={form.company} onChange={set("company")} placeholder="Brand / company name" />
      </div>
      <CategoryField value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} />
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-message`} className="text-navy">Message</Label>
        <Textarea data-testid="contact-message-input" id={`${idPrefix}-message`} value={form.message} onChange={set("message")} placeholder="Tell us about the product you want to build…" rows={4} className="bg-white border-navy/15" />
      </div>
      <SubmitButton submitting={submitting} />
    </form>
  );
}
